// Model catalog maintenance (master email only).
//
// The catalog is broadcast to every hub, so the risky operation here is not
// adding a model — it is an upload that OMITS one. An upload replaces the live
// catalog, and anything missing from the file is withdrawn, which marks it
// unavailable in every install. So the flow is upload → preview → confirm, and
// the withdrawal list is the part the UI puts in front of you.

import React, { useState, useEffect, useRef } from 'react';
import { Upload, RefreshCw, AlertTriangle, Check, X, Archive, ChevronUp } from 'lucide-react';
import { api } from '../services/api';
import type { CatalogModel, CatalogDiff } from '../types';

function formatCost(v: number): string {
  if (v === 0) return '—';
  return `$${v.toFixed(2)}`;
}

function formatTokens(v: number | null): string {
  if (v === null || v === 0) return '—';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${Math.round(v / 1_000)}K`;
  return String(v);
}

const CATEGORY_STYLES: Record<string, string> = {
  premium: 'bg-purple-500/10 text-purple-300 border-purple-500/30',
  standard: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
  budget: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
};

export function ModelCatalogManager() {
  const [models, setModels] = useState<CatalogModel[]>([]);
  const [archivedIds, setArchivedIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  // Upload state
  const [fileName, setFileName] = useState<string | null>(null);
  const [pendingModels, setPendingModels] = useState<unknown[] | null>(null);
  const [diff, setDiff] = useState<CatalogDiff | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [isBusy, setIsBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadCatalog();
  }, []);

  const loadCatalog = async () => {
    try {
      const result = await api.listCatalog();
      setModels(result.models);
      setArchivedIds(result.archivedIds);
    } catch {
      // Leave the table as-is; the operator can retry with Refresh.
    } finally {
      setIsLoading(false);
    }
  };

  const resetUpload = () => {
    setFileName(null);
    setPendingModels(null);
    setDiff(null);
    setProblems([]);
    if (fileInput.current) fileInput.current.value = '';
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setDiff(null);
    setProblems([]);
    setIsBusy(true);

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      // Accept the committed catalog/models.json shape or a bare array, so the
      // file in git uploads without editing.
      const list: unknown[] = Array.isArray(parsed) ? parsed : parsed?.models;

      if (!Array.isArray(list)) {
        setProblems(['File must be a JSON array of models, or an object with a "models" array.']);
        return;
      }

      const result = await api.previewCatalog(list);
      setProblems(result.problems ?? []);
      if (result.valid && result.diff) {
        setPendingModels(list);
        setDiff(result.diff);
      }
    } catch (err) {
      setProblems([err instanceof Error ? err.message : 'Could not read that file as JSON.']);
    } finally {
      setIsBusy(false);
    }
  };

  const handleApply = async () => {
    if (!pendingModels) return;
    setIsBusy(true);
    try {
      const result = await api.applyCatalog(pendingModels);
      if (!result.valid) {
        setProblems(result.problems ?? ['The catalog was rejected.']);
        return;
      }
      resetUpload();
      await loadCatalog();
    } catch {
      setProblems(['Failed to apply the catalog.']);
    } finally {
      setIsBusy(false);
    }
  };

  const handleWithdraw = async (id: string) => {
    if (
      !confirm(
        `Withdraw "${id}"?\n\nEvery hub will mark it unavailable. It is archived, not ` +
          `deleted — assistants already using it keep working, and re-uploading a ` +
          `catalog that names it will restore it.`,
      )
    ) {
      return;
    }

    setIsBusy(true);
    try {
      await api.withdrawCatalogModel(id);
      await loadCatalog();
    } catch {
      alert('Failed to withdraw the model.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="mt-12">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-medium text-accent uppercase tracking-wider">Model Catalog</p>
          <p className="text-sm text-muted-foreground mt-1">
            Broadcast to every hub. Upload replaces the catalog — anything missing from the
            file is withdrawn.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadCatalog}
            disabled={isBusy}
            className="text-muted-foreground hover:text-foreground transition-colors p-2 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-muted-foreground hover:text-foreground transition-colors p-2"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            <ChevronUp className={`w-4 h-4 transition-transform ${expanded ? '' : 'rotate-180'}`} />
          </button>
        </div>
      </div>

      {expanded && (
        <>
          {/* ── Upload ── */}
          <div className="bg-card border border-border rounded-lg p-4 mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <input
                ref={fileInput}
                type="file"
                accept="application/json,.json"
                onChange={handleFile}
                className="hidden"
                id="catalog-upload"
              />
              <label
                htmlFor="catalog-upload"
                className="inline-flex items-center gap-2 bg-secondary hover:bg-secondary/80 border border-border rounded-md px-3 py-2 text-sm cursor-pointer transition-colors"
              >
                <Upload className="w-4 h-4" />
                Choose catalog JSON
              </label>
              {fileName && <span className="text-sm text-muted-foreground font-mono">{fileName}</span>}
              {isBusy && <span className="text-sm text-muted-foreground">Working…</span>}
              {(fileName || diff) && (
                <button
                  onClick={resetUpload}
                  className="text-muted-foreground hover:text-foreground text-sm ml-auto"
                >
                  Cancel
                </button>
              )}
            </div>

            {problems.length > 0 && (
              <div className="mt-4 border border-red-500/30 bg-red-500/5 rounded-md p-3">
                <div className="flex items-center gap-2 text-red-300 text-sm font-medium mb-2">
                  <X className="w-4 h-4" />
                  Nothing was changed — {problems.length} problem
                  {problems.length === 1 ? '' : 's'} in that file
                </div>
                <ul className="text-xs text-red-200/80 space-y-1 font-mono">
                  {problems.map((p, i) => (
                    <li key={i}>• {p}</li>
                  ))}
                </ul>
              </div>
            )}

            {diff && (
              <div className="mt-4 border border-border rounded-md p-3 bg-secondary/40">
                <p className="text-sm font-medium mb-3">Review before applying</p>

                <div className="grid grid-cols-4 gap-3 text-center mb-3">
                  <Stat label="Added" value={diff.added.length} tone="text-emerald-300" />
                  <Stat label="Updated" value={diff.updated.length} tone="text-blue-300" />
                  <Stat label="Withdrawn" value={diff.withdrawn.length} tone="text-amber-300" />
                  <Stat label="Unchanged" value={diff.unchanged.length} tone="text-muted-foreground" />
                </div>

                {/* The consequential part: these disappear from every hub's picker. */}
                {diff.withdrawn.length > 0 && (
                  <div className="border border-amber-500/30 bg-amber-500/5 rounded-md p-3 mb-3">
                    <div className="flex items-center gap-2 text-amber-300 text-sm font-medium mb-2">
                      <AlertTriangle className="w-4 h-4" />
                      {diff.withdrawn.length} model{diff.withdrawn.length === 1 ? '' : 's'} will be
                      withdrawn
                    </div>
                    <p className="text-xs text-amber-200/70 mb-2">
                      Not in the uploaded file. Every hub will mark these unavailable. They are
                      archived rather than deleted, so assistants already using them keep working.
                    </p>
                    <ul className="text-xs font-mono text-amber-200/90 space-y-0.5">
                      {diff.withdrawn.map((id) => (
                        <li key={id}>• {id}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {diff.added.length > 0 && (
                  <DiffList title="Added" ids={diff.added} tone="text-emerald-200/90" />
                )}

                {diff.updated.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      Updated
                    </p>
                    <ul className="text-xs font-mono space-y-1">
                      {diff.updated.map((u) => (
                        <li key={u.id} className="text-blue-200/90">
                          • {u.id}
                          <ul className="ml-4 text-muted-foreground">
                            {u.changes.map((ch, i) => (
                              <li key={i}>{ch}</li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <button
                  onClick={handleApply}
                  disabled={isBusy}
                  className="inline-flex items-center gap-2 bg-accent text-accent-foreground rounded-md px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
                >
                  <Check className="w-4 h-4" />
                  Apply to the live catalog
                </button>
              </div>
            )}
          </div>

          {/* ── Live models ── */}
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : models.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <p className="text-sm text-muted-foreground">
                The catalog is empty. Upload a JSON file to publish models.
              </p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="px-4 py-3">Model</th>
                      <th className="px-4 py-3">Provider</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3 text-right">In / 1M</th>
                      <th className="px-4 py-3 text-right">Out / 1M</th>
                      <th className="px-4 py-3 text-right">Context</th>
                      <th className="px-4 py-3 text-right">Max out</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {models.map((m) => (
                      <tr key={m.id} className="border-b border-border/50 last:border-0">
                        <td className="px-4 py-3">
                          <div className="font-medium">{m.name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{m.id}</div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{m.provider}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full border ${
                              CATEGORY_STYLES[m.category] ?? 'border-border text-muted-foreground'
                            }`}
                          >
                            {m.category}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs">
                          {formatCost(m.inputCostPer1M)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs">
                          {formatCost(m.outputCostPer1M)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs">
                          {formatTokens(m.contextWindow)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs">
                          {formatTokens(m.maxOutputTokens)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleWithdraw(m.id)}
                            disabled={isBusy}
                            className="text-muted-foreground hover:text-amber-300 transition-colors p-1 disabled:opacity-50"
                            title="Withdraw from the broadcast"
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">
                {models.length} model{models.length === 1 ? '' : 's'} broadcast
                {archivedIds.length > 0 && (
                  <> · {archivedIds.length} withdrawn ({archivedIds.join(', ')})</>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="bg-card border border-border rounded-md py-2">
      <div className={`text-lg font-semibold ${tone}`}>{value}</div>
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
    </div>
  );
}

function DiffList({ title, ids, tone }: { title: string; ids: string[]; tone: string }) {
  return (
    <div className="mb-3">
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{title}</p>
      <ul className={`text-xs font-mono space-y-0.5 ${tone}`}>
        {ids.map((id) => (
          <li key={id}>• {id}</li>
        ))}
      </ul>
    </div>
  );
}
